/**
 * Task Dialog
 *
 * Dialog for creating/editing schedulable tasks with title, description,
 * deadline, estimated duration, priority, and calendar selection.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  SchedulableTaskDetail,
  SchedulableTaskInput,
  CalendarCollectionDetail,
} from '@/lib/api';
import { resolveNewEventCalendarId } from '../utils';

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: SchedulableTaskInput) => Promise<void>;
  calendars: CalendarCollectionDetail[];
  editTask?: SchedulableTaskDetail | null;
  defaultTaskDeadlineOffset?: number | null;
}

export function TaskDialog({
  open,
  onClose,
  onSave,
  calendars,
  editTask,
  defaultTaskDeadlineOffset,
}: TaskDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState(60);
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [calendarId, setCalendarId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editTask) {
        setTitle(editTask.title);
        setDescription(editTask.description);
        setDeadline(editTask.deadline.slice(0, 16)); // datetime-local format
        setEstimatedMinutes(editTask.estimatedMinutes);
        setPriority(editTask.priority);
        setCalendarId(editTask.calendarId || '');
      } else {
        setTitle('');
        setDescription('');
        const d = new Date();
        d.setDate(d.getDate() + (defaultTaskDeadlineOffset ?? 7));
        setDeadline(d.toISOString().slice(0, 16));
        setEstimatedMinutes(60);
        setPriority('medium');
        setCalendarId(resolveNewEventCalendarId(calendars) ?? '');
      }
    }
  }, [open, editTask, calendars, defaultTaskDeadlineOffset]);

  const handleSubmit = async () => {
    if (!title.trim() || !deadline) return;

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        deadline: new Date(deadline).toISOString(),
        estimatedMinutes,
        priority,
        calendarId: calendarId || undefined,
      });
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editTask ? 'Edit Task' : 'New Schedulable Task'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="task-desc">Description</Label>
            <Textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="task-deadline">Deadline</Label>
              <Input
                id="task-deadline"
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="task-duration">Duration (minutes)</Label>
              <Input
                id="task-duration"
                type="number"
                min={1}
                value={estimatedMinutes}
                onChange={(e) => setEstimatedMinutes(parseInt(e.target.value, 10) || 60)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as typeof priority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Calendar</Label>
              <Select value={calendarId} onValueChange={setCalendarId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      {cal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || !deadline || saving}>
            {saving ? 'Saving...' : editTask ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
